// /frontend/src/services/api.service.ts

/**
 * Central API service module.
 * This file configures and exports a global Axios instance for making API requests.
 * It handles the base URL, headers, and interceptors for authentication and error handling.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

/**
 * The global Axios instance.
 * It's initialized as null and will be configured by the `initApiService` function.
 *
 * @type {AxiosInstance | null}
 */
let apiClient: AxiosInstance | null = null;
let apiBaseUrl = '/api';

/**
 * Initializes the API client configuration.
 * This is called once in main.ts to set the base URL from environment variables.
 * @param baseURL The base URL for all API requests.
 * @author Alexander Funke
 * @lastChange 2025-10-02
 */
export function initApiService(baseURL: string): void {
  apiBaseUrl = baseURL;
}

/**
 * Creates and configures the Axios instance on its first use (Lazy Initialization).
 *
 * @returns {AxiosInstance} The configured Axios instance.
 *
 * @author Alexander Funke
 * @lastChange 2025-10-06
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: apiBaseUrl,
    headers: { 'Content-Type': 'application/json' },
  });

  /**
   * Request Interceptor:
   * This function is called before every request is sent.
   * Its primary purpose is to attach the JWT access token to the Authorization header.
   */
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const authStore = useAuthStore();
      if (authStore.accessToken) {
        config.headers.Authorization = `Bearer ${authStore.accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  /**
   * Response Interceptor:
   * Handles global API errors. Its primary role is to transparently handle
   * access token refreshes when a 401 Unauthorized error occurs on a protected endpoint.
   */
  client.interceptors.response.use(
    // On success, simply return the response.
    (response) => response,

    // On error, inspect the error and decide what to do.
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check if the error is a 401 Unauthorized and that we have an original request.
      if (error.response?.status === 401 && originalRequest) {

        // --- IMPORTANT: Differentiate the cause of the 401 ---
        // If the 401 error came from an authentication attempt (e.g., login),
        // it means "invalid credentials", not "expired token". We should not try to refresh.
        if (originalRequest.url?.includes('/auth/')) {
          // Just reject the promise. The calling component (e.g., SignInForm)
          // is responsible for handling a login failure.
          return Promise.reject(error);
        }

        // If we reach here, it's a 401 from a protected endpoint, likely due to an expired access token.
        // We should only attempt to refresh the token once per request.
        if (!originalRequest._retry) {
          originalRequest._retry = true; // Mark the request to prevent infinite refresh loops.
          const authStore = useAuthStore();

          try {
            console.log('Access token may have expired. Attempting to refresh...');
            await authStore.refreshTokenAction();

            // The request interceptor will automatically add the new access token.
            // Now, retry the original request with the updated token.
            if (apiClient) {
              return apiClient(originalRequest);
            }
          } catch (refreshError) {
            // If refreshTokenAction throws an error, it means the refresh token is also invalid.
            // The store's action should have already triggered a logout. We just pass the error along.
            console.error("Token refresh failed. The user will be logged out.");
            return Promise.reject(refreshError);
          }
        }
      }

      // For all other errors (403, 404, 500, etc.) or if the refresh logic fails,
      // log the error and reject the promise.
      console.error('[API Response Error]', error.response?.data || error.message);
      return Promise.reject(error);
    }
  );

  return client;
}

/**
 * Provides access to the configured API client.
 * It initializes the client on the first call.
 *
 * @returns {AxiosInstance} The configured Axios instance.
 *
 * @author Alexander Funke
 * @lastChange 2025-10-06
 */
export function useApi(): AxiosInstance {
  if (!apiClient) {
    apiClient = createApiClient();
  }
  return apiClient;
}
